<template>
    <div class="flex items-center min-w-0 flex-shrink-0">
        <Popover v-model:open="isPopoverOpen" :modal="modalPopover" :disabled="isComponentDisabled">
            <PopoverTrigger :asChild="asChild">
                <template v-if="asChild">
                    <span class="inline-block">
                        <slot />
                    </span>
                </template>
                <template v-else>
                    <Button
                        ref="triggerRef"
                        v-bind="attrs"
                        type="button"
                        role="combobox"
                        :aria-expanded="isPopoverOpen.toString()"
                        aria-haspopup="listbox"
                        :disabled="isComponentDisabled"
                        :class="
                            cn(
                                'flex flex-1 min-w-0 pl-3 pr-2 rounded-md border justify-between overflow-hidden',
                                scrollableInput ? 'h-9 items-center py-2' : 'min-h-9 items-center py-1',
                                'bg-transparent dark:bg-input/30 shadow-xs transition-[color,box-shadow]',
                                'hover:bg-input/10 dark:hover:bg-input/40',
                                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                                className
                            )
                        "
                        >
                        <template v-if="selectedValues.length">
                            <div class="flex justify-between items-center w-full">
                                <!-- Show individual tags or count based on showTags prop -->
                                <div v-if="showTags" class="flex-1 mr-2 min-w-0" :class="scrollableInput ? 'overflow-hidden' : 'overflow-visible'">
                                    <div
                                        class="flex flex-wrap items-center gap-1"
                                        :class="scrollableInput ? 'overflow-x-auto overflow-y-hidden max-h-7' : 'overflow-visible'">
                                        <Badge
                                            v-for="value in visibleValues"
                                            :key="value"
                                            :title="optionMap[value]?.label"
                                            :class="
                                                cn(
                                                    isAnimating && 'animate-bounce',
                                                    multiSelectVariants({ variant }),
                                                    'dark:border-foreground/20 dark:bg-card/90 dark:text-card-foreground flex-shrink-0 max-w-32 inline-flex items-center'
                                                )
                                            "
                                            :style="{ animationDuration: animation + 's' }">
                                            <component :is="optionMap[value].icon" class="h-4 w-4 mr-2 flex-shrink-0" />
                                            <span class="truncate">{{ truncateLabel(optionMap[value]?.label) }}</span>
                                            <div @click.stop.prevent="handleBadgeClick(value)">
                                                <XCircleIcon
                                                    :class="[
                                                        'ml-2 h-4 w-4 flex-shrink-0',
                                                        isComponentDisabled
                                                            ? 'cursor-not-allowed opacity-50 pointer-events-none'
                                                            : 'pointer-events-auto cursor-pointer'
                                                    ]" />
                                            </div>
                                        </Badge>

                                        <Badge
                                            v-if="overflowCount > 0"
                                            :class="
                                                cn(
                                                    'bg-transparent text-foreground border-foreground/20 hover:bg-transparent whitespace-nowrap flex-shrink-0',
                                                    isAnimating && 'animate-bounce',
                                                    multiSelectVariants({ variant }),
                                                    'dark:border-foreground/20 dark:text-foreground/80'
                                                )
                                            "
                                            :style="{ animationDuration: animation + 's' }">
                                            + {{ overflowCount }} more
                                            <div @click.stop.prevent="handleClearExtraClick">
                                                <XCircleIcon
                                                    :class="[
                                                        'ml-2 h-4 w-4',
                                                        isComponentDisabled
                                                            ? 'cursor-not-allowed opacity-50 pointer-events-none'
                                                            : 'pointer-events-auto cursor-pointer'
                                                    ]" />
                                            </div>
                                        </Badge>
                                    </div>
                                </div>

                                <!-- Show count only when showTags is false -->
                                <div v-else class="flex justify-left">
                                    <span class="text-sm text-foreground">
                                        {{ selectedValues.length }} {{ selectedValues.length === 1 ? 'item' : 'items' }} selected
                                    </span>
                                </div>

                                <div class="flex items-center flex-shrink-0">
                                    <div @pointerdown.stop.prevent="handleMainClearClick">
                                        <XIcon
                                            data-clear-button
                                            :class="[
                                                'h-4 mx-1 text-muted-foreground dark:text-muted-foreground/70',
                                                isComponentDisabled
                                                    ? 'cursor-not-allowed opacity-50 pointer-events-none'
                                                    : 'pointer-events-auto cursor-pointer'
                                            ]" />
                                    </div>
                                    <Separator orientation="vertical" class="flex min-h-6 h-full" />
                                    <div>
                                        <ChevronDownIcon class="h-4 ml-2 cursor-pointer text-muted-foreground dark:text-muted-foreground/70" />
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div class="flex items-center justify-between w-full mx-auto">
                                <span class="text-sm text-muted-foreground dark:text-muted-foreground/70">
                                    {{ placeholder }}
                                </span>
                                <ChevronDownIcon class="h-4 cursor-pointer text-muted-foreground dark:text-muted-foreground/70 mx-1" />
                            </div>
                        </template>
                    </Button>
                </template>
            </PopoverTrigger>

            <PopoverContent class="p-2" :align="popoverAlign" :avoid-collisions="true" :collision-padding="8" @keydown.esc="isPopoverOpen = false">
                <Command>
                    <CommandInput
                        placeholder="Search..."
                        @keydown="handleInputKeyDown"
                        :disabled="isComponentDisabled"
                        class="border-b dark:border-border" />

                    <!-- Scrollable list -->
                    <CommandList :style="{ maxHeight: listMaxHeight }">
                        <CommandEmpty>No results found.</CommandEmpty>

                        <CommandGroup>
                            <CommandItem
                                v-if="optionsInternal.length > 1 && showSelectAll"
                                value="__select_all__"
                                @select="toggleAll"
                                :class="[isComponentDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer']"
                                :hide-on-search="true">
                                <Checkbox :checked="isAllSelected" :disabled="isComponentDisabled" class="mr‑2" />
                                <span class="font-bold">Select All</span>
                            </CommandItem>
                            <CommandItem
                                v-for="opt in optionsInternal"
                                :key="opt.value"
                                :value="opt.value"
                                @select="() => !disabledOptions.includes(opt.value) && !isComponentDisabled && toggleOption(opt.value)"
                                :class="[
                                    disabledOptions.includes(opt.value) || isComponentDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                ]">
                                <div class="flex items-center w-full">
                                    <!-- 1) Always‑show Checkbox -->
                                    <Checkbox
                                        :checked="selectedValues.includes(opt.value)"
                                        :disabled="disabledOptions.includes(opt.value) || isComponentDisabled"
                                        class="mr-2" />

                                    <!-- 2) User‑customisable remainder of the option -->
                                    <slot name="option" :option="opt.raw ?? opt">
                                        <!-- default: icon + label -->
                                        <component :is="opt.icon" class="mr-2 h-4 w-4 text-muted-foreground dark:text-muted-foreground/70" />
                                        <span class="flex-1 min-w-0 truncate" :title="opt.label">{{ opt.label }}</span>
                                    </slot>
                                </div>
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>

                <!-- footer actions -->

                <slot name="footer">
                    <div class="flex items-center justify-between border-t p-2 bg-background dark:bg-background">
                        <Button v-if="selectedValues.length" variant="ghost" size="sm" :disabled="isComponentDisabled" @click="handleClear">
                            Clear
                        </Button>
                        <Button variant="ghost" size="sm" @click="isPopoverOpen = false"> Close </Button>
                    </div>
                </slot>
            </PopoverContent>
        </Popover>

        <LoadingButton
            v-if="enableRefresh"
            :size="scrollableInput ? 'md' : 'md'"
            :class="cn('ml-1 flex-shrink-0', scrollableInput ? 'h-9' : 'min-h-9')"
            :is-loading="refreshLoading"
            :disabled="props.disabled || props.loading"
            :show-content-on-loading="false"
            @click="$emit('refresh')">
            <RefreshCwIcon class="h-4 w-4 text-primary-foreground dark:text-primary-foreground/70" />
        </LoadingButton>
    </div>
</template>

<script setup>
import { ref, computed, useAttrs, watch } from 'vue';
import { cva } from 'class-variance-authority';
import { XCircleIcon, ChevronDownIcon, XIcon, RefreshCwIcon } from 'lucide-vue-next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import LoadingButton from '../loading-button/LoadingButton.vue';

const attrs = useAttrs();

const props = defineProps({
    options: { type: Array, required: true },
    modelValue: { type: Array, default: () => [] },
    variant: { type: String, default: 'default' },
    placeholder: { type: String, default: 'Select options' },
    animation: { type: Number, default: 0 },
    maxCount: { type: Number, default: 2 },
    listMaxHeight: { type: String, default: '16rem' }, // default list height
    modalPopover: { type: Boolean, default: false },
    asChild: { type: Boolean, default: false },
    className: { type: String, default: '' },
    enableRefresh: { type: Boolean, default: false },
    refreshLoading: { type: Boolean, default: false },
    trackBy: { type: String, default: null },
    optionLabel: { type: [String, Function], default: null },
    showTags: { type: Boolean, default: true }, // Show individual tags or just count
    maxTagLength: { type: Number, default: 12 }, // Max characters before truncation
    showSelectAll: { type: Boolean, default: true }, // Show the built-in "Select All" option
    disabledOptions: { type: Array, default: () => [] }, // Array of values that should be disabled
    scrollableInput: { type: Boolean, default: false }, // If true, input stays fixed height and scrolls; if false, expands
    disabled: { type: Boolean, default: false }, // Disable the entire component
    loading: { type: Boolean, default: false }, // Show loading state for the entire component
    popoverAlign: { type: String, default: 'center' } // Popover alignment: 'start', 'center', 'end'
});

const emit = defineEmits(['update:modelValue', 'refresh']);

const isPopoverOpen = ref(false);
const isAnimating = ref(false);
const triggerRef = ref(null);

// Computed property to determine if component should be disabled
const isComponentDisabled = computed(() => props.disabled || props.loading || props.refreshLoading);

// Use the popoverAlign prop, defaulting to 'center' for better positioning
const popoverAlign = computed(() => props.popoverAlign);

function sameArray(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}

const selectedValues = ref([...props.modelValue]);

watch(
    () => props.modelValue,
    (newVal) => {
        if (!sameArray(newVal, selectedValues.value)) {
            selectedValues.value = [...newVal];
        }
    }
);

watch(selectedValues, (vals) => {
    if (!sameArray(vals, props.modelValue)) {
        emit('update:modelValue', [...vals]);
    }
});

// derive internal options
const optionsInternal = computed(() => {
    return props.options.map((o) => {
        const val = props.trackBy ? o[props.trackBy] : (o.value ?? o);
        const lab = props.optionLabel ? (typeof props.optionLabel === 'function' ? props.optionLabel(o) : o[props.optionLabel]) : (o.label ?? val);
        return { value: val, label: lab, ...(o.icon ? { icon: o.icon } : {}), raw: o };
    });
});

const optionMap = computed(() => Object.fromEntries(optionsInternal.value.map((o) => [o.value, o])));

const visibleValues = computed(() => selectedValues.value.slice(0, props.maxCount));
const overflowCount = computed(() => Math.max(0, selectedValues.value.length - props.maxCount));
const isAllSelected = computed(() => selectedValues.value.length === optionsInternal.value.length);

// Function to truncate label text with ellipsis
const truncateLabel = (label) => {
    if (!label || label.length <= props.maxTagLength) return label;
    return label.substring(0, props.maxTagLength) + '...';
};

function handleInputKeyDown(e) {
    if (isComponentDisabled.value) return;

    if (e.key === 'Enter') {
        isPopoverOpen.value = true;
    } else if (e.key === 'Backspace' && !e.target.value) {
        const arr = [...selectedValues.value];
        arr.pop();
        selectedValues.value = arr;
    }
}

function toggleOption(val) {
    if (isComponentDisabled.value) return;

    const arr = selectedValues.value.includes(val) ? selectedValues.value.filter((v) => v !== val) : [...selectedValues.value, val];
    selectedValues.value = arr;
}

function handleClear() {
    if (isComponentDisabled.value) return;

    selectedValues.value = [];
}

function clearExtraOptions() {
    if (isComponentDisabled.value) return;

    selectedValues.value = selectedValues.value.slice(0, props.maxCount);
}

function toggleAll() {
    if (isComponentDisabled.value) return;

    if (isAllSelected.value) {
        handleClear();
    } else {
        selectedValues.value = optionsInternal.value.map((o) => o.value);
    }
}

// Event handlers for click prevention
function handleBadgeClick(value) {
    if (!isComponentDisabled.value) {
        toggleOption(value);
    }
}

function handleClearExtraClick() {
    if (!isComponentDisabled.value) {
        clearExtraOptions();
    }
}

function handleMainClearClick() {
    if (!isComponentDisabled.value) {
        handleClear();
    }
}

const multiSelectVariants = cva('m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300', {
    variants: {
        variant: {
            default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
            secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
            destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
            inverted: 'inverted'
        }
    },
    defaultVariants: { variant: 'default' }
});
</script>
